import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Folder, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import TopBar from '../components/TopBar';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATIC_HEADER = { cta_url: "/donate" };
const STATIC_FOOTER = { contact_email: "info@securethevotemd.com" };

const ArticleCard = ({ article, index }) => {
  const date = article.published_at ? new Date(article.published_at) : null;
  const isExternal = Boolean(article.external_url);
  const cardHref = isExternal ? article.external_url : `/blog/${article.slug}`;
  const CardLink = isExternal ? 'a' : Link;
  const linkProps = isExternal
    ? { href: cardHref, target: '_blank', rel: 'noopener noreferrer' }
    : { to: cardHref };

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      viewport={{ once: true, margin: "-40px" }}
      className="bg-white rounded-lg overflow-hidden shadow-md group"
      data-testid={`news-card-${index}`}
    >
      <CardLink {...linkProps}>
        <div className="relative overflow-hidden">
          {article.featured_image ? (
            <img
              src={article.featured_image}
              alt={article.title}
              className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-48 bg-gradient-to-br from-[#9E1B32] to-[#6B1029] flex items-center justify-center">
              <span className="text-white/30 text-6xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>STV</span>
            </div>
          )}
          {date && (
            <div className="absolute bottom-0 left-0 bg-[#9E1B32] text-white px-4 py-2">
              <span className="block text-xl font-bold">
                {date.toLocaleDateString('en-US', { day: '2-digit' })}
              </span>
              <span className="text-sm">
                {date.toLocaleDateString('en-US', { month: 'short' })}
              </span>
            </div>
          )}
        </div>
        <div className="p-6">
          <div className="flex items-center gap-2 text-slate-500 text-sm mb-3">
            <Folder size={14} />
            <span>{article.category}</span>
          </div>
          <h3 className="text-lg font-bold text-slate-900 group-hover:text-[#9E1B32] transition-colors mb-3 line-clamp-2">
            {article.title}
          </h3>
          <p className="text-slate-600 text-sm line-clamp-2 mb-4">
            {article.excerpt}
          </p>
          <span className="inline-flex items-center gap-2 text-[#9E1B32] font-semibold text-sm group-hover:gap-3 transition-all duration-300">
            {isExternal ? 'READ ARTICLE' : 'READ MORE'}
            <ArrowRight size={14} />
          </span>
        </div>
      </CardLink>
    </motion.article>
  );
};

const CategoryNewsPage = ({ category, categories, postType, postTypes, pageTitle }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '1');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearch, setActiveSearch] = useState('');

  const [data, setData] = useState({ posts: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set('page', currentPage);
    params.set('limit', '12');
    if (categories) {
      params.set('categories', categories);
    } else if (category) {
      params.set('category', category);
    }
    if (postTypes) {
      params.set('post_types', postTypes);
    } else if (postType) {
      params.set('post_type', postType);
    }
    if (activeSearch) {
      params.set('search', activeSearch);
    }

    axios.get(`${API_URL}/api/posts/list?${params.toString()}`)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [currentPage, category, categories, postType, postTypes, activeSearch]);

  const handleSearch = (e) => {
    e.preventDefault();
    setActiveSearch(searchQuery);
    setSearchParams({});
  };

  const clearSearch = () => {
    setSearchQuery('');
    setActiveSearch('');
    setSearchParams({});
  };

  const setPage = (p) => {
    const params = new URLSearchParams();
    params.set('page', p);
    setSearchParams(params);
    window.scrollTo(0, 0);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="news-page">
      <TopBar />
      <Header config={STATIC_HEADER} />

      {/* Hero Banner */}
      <div className="relative pt-[50px]" style={{ backgroundColor: '#9E1B32' }}>
        <div className="py-16 sm:py-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl text-center">
            <motion.h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white"
              style={{ fontFamily: "'Playfair Display', serif" }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {pageTitle}
            </motion.h1>
            <motion.div
              className="w-16 h-1 bg-[#F6BF58] mx-auto mt-6"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            />
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="border-b bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-4">
          <form onSubmit={handleSearch} className="flex gap-2 max-w-lg mx-auto">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${pageTitle.toLowerCase()}...`}
                className="pl-9 h-10"
                data-testid="news-search-input"
              />
            </div>
            <Button type="submit" size="sm" className="h-10 px-5" style={{ backgroundColor: '#9E1B32' }} data-testid="news-search-btn">
              Search
            </Button>
            {activeSearch && (
              <Button type="button" variant="outline" size="sm" className="h-10" onClick={clearSearch}>
                Clear
              </Button>
            )}
          </form>
          {activeSearch && (
            <p className="text-center text-sm text-slate-500 mt-2">
              Showing results for "<span className="font-medium text-slate-700">{activeSearch}</span>" — {data.total} found
            </p>
          )}
        </div>
      </div>

      {/* Articles Grid */}
      <main className="py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg overflow-hidden shadow-md">
                  <div className="h-48 bg-slate-100 animate-pulse" />
                  <div className="p-6 space-y-3">
                    <div className="h-4 bg-slate-100 rounded animate-pulse w-1/3" />
                    <div className="h-5 bg-slate-100 rounded animate-pulse w-full" />
                    <div className="h-4 bg-slate-100 rounded animate-pulse w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : data.posts.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              No articles found.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {data.posts.map((article, index) => (
                  <ArticleCard key={article.id} article={article} index={index} />
                ))}
              </div>

              {/* Pagination */}
              {data.pages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-12" data-testid="pagination">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage <= 1}
                    onClick={() => setPage(currentPage - 1)}
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  {Array.from({ length: Math.min(data.pages, 7) }, (_, i) => {
                    let pageNum;
                    if (data.pages <= 7) {
                      pageNum = i + 1;
                    } else if (currentPage <= 4) {
                      pageNum = i + 1;
                    } else if (currentPage >= data.pages - 3) {
                      pageNum = data.pages - 6 + i;
                    } else {
                      pageNum = currentPage - 3 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={pageNum === currentPage ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setPage(pageNum)}
                        style={pageNum === currentPage ? { backgroundColor: '#9E1B32' } : {}}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage >= data.pages}
                    onClick={() => setPage(currentPage + 1)}
                  >
                    <ChevronRight size={16} />
                  </Button>
                </div>
              )}

              <div className="text-center text-sm text-slate-500 mt-4">
                Showing {(currentPage - 1) * 12 + 1}–{Math.min(currentPage * 12, data.total)} of {data.total} articles
              </div>
            </>
          )}
        </div>
      </main>

      <Footer config={STATIC_FOOTER} />
    </div>
  );
};

// Each category maps to actual DB category values
export const PressReleasePage = () => <CategoryNewsPage categories="press-release,spotlight-series" postType="press-release" pageTitle="Press Releases" />;
export const InTheNewsPage = () => <CategoryNewsPage category="in-the-news" pageTitle="In the News" />;
export const AllNewsPage = () => <CategoryNewsPage pageTitle="News" />;

export default CategoryNewsPage;
